import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Footer from "components/HomePage/Footer";
import Header from "components/HomePage/Header";
import { AlertCircle, ArrowRight, Eye, EyeOff, Lock, Mail } from "lucide-react";
import React, { useState } from "react";
import { connect } from "react-redux";
import { Navigate } from "react-router";
import { bindActionCreators } from "redux";
import {
  removeSignInActionFailure,
  signInAction,
} from "../../store/Signin/actions";
import { isUserSigninFailed, isUserSigninSucceed } from "../../store/selectors";
import { Dispatch, RootState } from "../../store/types";

export interface SignInProps {
  isUserSigninSucceed: boolean;
  isUserSigninFailed: boolean;
  signIn: typeof signInAction;
  removeSignInFailure: typeof removeSignInActionFailure;
}

const _SignIn: React.FC<SignInProps> = ({
  isUserSigninSucceed,
  isUserSigninFailed,
  signIn,
  removeSignInFailure,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [inputEmail, setInputEmail] = useState("");
  const [inputPassword, setInputPassword] = useState("");
  const [inputRememberMe, setInputRememberMe] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    signIn(inputEmail, inputPassword);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {isUserSigninSucceed && <Navigate to="/feature" replace />}
      <main>
        <section className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Left side - Branding */}
              <div className="text-center lg:text-left">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  Bon retour sur{" "}
                  <span className="text-blue-600">Docuveritas</span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Accédez à votre espace personnel pour continuer vos recherches
                  juridiques et consulter vos documents certifiés.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <Lock className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-gray-700">
                      Connexion sécurisée SSL
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-gray-700">
                      Accès à tous vos documents
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <ArrowRight className="h-4 w-4 text-purple-600" />
                    </div>
                    <span className="text-gray-700">
                      Synchronisation multi-appareils
                    </span>
                  </div>
                </div>
              </div>

              {/* Right side - Login Form */}
              <div className="max-w-md mx-auto w-full">
                <Card className="border-2 border-gray-200 rounded-2xl shadow-xl">
                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl font-bold text-gray-900">
                      Connexion
                    </CardTitle>
                    <p className="text-gray-600 mt-2">
                      Accédez à votre compte Docuveritas
                    </p>
                  </CardHeader>

                  <CardContent className="px-8 pb-8">
                    {(error || isUserSigninFailed) && (
                      <Alert className="mb-6 border-red-200 bg-red-50 relative">
                        <AlertCircle className="h-4 w-4 text-red-600" />
                        <AlertDescription className="text-red-700">
                          {error || "Email ou mot de passe incorrects!"}
                        </AlertDescription>
                        <button
                          onClick={() => {
                            setError("");
                            if (isUserSigninFailed) {
                              removeSignInFailure();
                            }
                          }}
                          className="absolute top-2 right-2 text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <Label
                          htmlFor="email"
                          className="text-sm font-medium text-gray-700"
                        >
                          Adresse email
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={inputEmail}
                          onChange={(e) => setInputEmail(e.target.value)}
                          className="mt-1 border-2 border-gray-200 focus:border-slate-500 rounded-xl"
                          placeholder="votre.email@entreprise.com"
                        />
                      </div>

                      <div>
                        <Label
                          htmlFor="password"
                          className="text-sm font-medium text-gray-700"
                        >
                          Mot de passe
                        </Label>
                        <div className="relative mt-1">
                          <Input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            required
                            value={inputPassword}
                            onChange={(e) => setInputPassword(e.target.value)}
                            className="border-2 border-gray-200 focus:border-slate-500 rounded-xl pr-12"
                            placeholder="Votre mot de passe"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showPassword ? (
                              <EyeOff className="h-5 w-5" />
                            ) : (
                              <Eye className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="rememberMe"
                            checked={inputRememberMe}
                            onCheckedChange={(checked) =>
                              setInputRememberMe(checked as boolean)
                            }
                          />
                          <Label
                            htmlFor="rememberMe"
                            className="text-sm text-gray-600"
                          >
                            Se souvenir de moi
                          </Label>
                        </div>
                        <a
                          href="/mot-de-passe-oublie"
                          className="text-sm text-slate-500 hover:text-slate-600 hover:underline"
                        >
                          Mot de passe oublié ?
                        </a>
                      </div>

                      <Button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-slate-500 hover:bg-slate-600 text-white py-3 rounded-xl text-lg"
                      >
                        {isLoading ? (
                          <div className="flex items-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Connexion en cours...</span>
                          </div>
                        ) : (
                          <>
                            Se connecter
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </>
                        )}
                      </Button>
                    </form>

                    <div className="mt-8 pt-6 border-t border-gray-200 text-center">
                      <p className="text-gray-600">
                        Pas encore de compte ?{" "}
                        <a
                          href="/signup"
                          className="text-blue-600 font-semibold hover:text-blue-700 hover:underline"
                        >
                          Créer un compte
                        </a>
                      </p>
                    </div>

                    <div className="mt-6 text-center">
                      <p className="text-xs text-gray-500">
                        En vous connectant, vous acceptez nos{" "}
                        <a
                          href="/cgu"
                          className="text-blue-500 hover:underline"
                        >
                          Conditions d'utilisation
                        </a>{" "}
                        et notre{" "}
                        <a
                          href="/confidentialite"
                          className="text-blue-500 hover:underline"
                        >
                          Politique de confidentialité
                        </a>
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Additional Help */}
                <div className="mt-8 text-center">
                  <p className="text-gray-600 mb-4">
                    Besoin d'aide pour vous connecter ?
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      variant="outline"
                      className="border-slate-500 text-slate-500 hover:bg-slate-50 bg-transparent"
                    >
                      Contacter le support
                    </Button>
                    <Button
                      variant="outline"
                      className="border-slate-500 text-slate-500 hover:bg-slate-50 bg-transparent"
                    >
                      Guide d'utilisation
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export const SignIn = connect(
  (state: RootState) => ({
    isUserSigninSucceed: isUserSigninSucceed(state),
    isUserSigninFailed: isUserSigninFailed(state),
  }),
  (dispatch: Dispatch) =>
    bindActionCreators(
      {
        signIn: signInAction,
        removeSignInFailure: removeSignInActionFailure,
      },
      dispatch
    )
)(_SignIn);

export default SignIn;
